import React, { createContext, useCallback, useContext, useState } from 'react';

// Toast types
export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  show: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);
export const globalToast: {
  error: (message: string) => void;
} = {
  error: (message: string) => {
    console.error(message);
  },
};

let toastId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = (id: number) => setToasts((toasts) => toasts.filter((t) => t.id !== id));

  const show = useCallback((message: string, type: ToastType = 'info') => {
    setToasts((toasts) => {
      if (toasts.some((item) => item.message === message && item.type === type)) {
        return toasts;
      }

      const id = ++toastId;
      setTimeout(() => remove(id), 3500);

      return [...toasts, { id, message, type }].slice(-5);
    });
  }, []);

  const success = useCallback((message: string) => show(message, 'success'), [show]);
  const error = useCallback((message: string) => show(message, 'error'), [show]);
  const info = useCallback((message: string) => show(message, 'info'), [show]);

  globalToast.error = error;

  return (
    <ToastContext.Provider value={{ show, success, error, info }}>
      {children}
      <div className="fixed z-50 bottom-4 right-4 flex flex-col gap-2 items-end">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`px-4 py-2 rounded shadow text-white transition-all ${
              toast.type === 'success' ? 'bg-green-600' : toast.type === 'error' ? 'bg-red-600' : 'bg-gray-800'
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
